import DecoratedIframe from "./DecoratedIframe";
import DecoratedPlane from "./DecoratedPlane";
import DecoratedVideo from "./DecoratedVideo";
// import MediaCuller from "./MediaCuller";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { isPointValidForRoomMesh, snapToGrid } from "../../constants/Helpers";
import {
  useModelStore,
  useRoomStore,
  useUserInterfaceStore,
} from "../../store";
import { FrontEdges } from "./FrontEdges";
import DeleteModal from "./DeleteModal";
import { IoClose, IoMove, IoExpand } from "react-icons/io5";

// Pulsing blue outline for clickable decorations in player mode
function LinkIndicatorEdges({ decor }) {
  const edgesRef = useRef();

  // Animate opacity for pulsing effect
  useFrame(({ clock }) => {
    if (edgesRef.current) {
      const pulse = 0.4 + Math.sin(clock.getElapsedTime() * 2) * 0.3;
      edgesRef.current.material.opacity = pulse;
    }
  });

  return (
    <mesh raycast={() => null}>
      <planeGeometry args={[decor.width, decor.height]} />
      <meshBasicMaterial visible={false} />
      <FrontEdges
        ref={edgesRef}
        scale={1.01}
        color="#60a5fa"
        lineWidth={10}
        transparent
        opacity={0.7}
      />
    </mesh>
  );
}

function DecorationMesh({ decor, selected, disableRaycast }) {
  const currentPlayerMode = useUserInterfaceStore((s) => s.playerMode);

  const edges = currentPlayerMode === "editor" && (
    <FrontEdges
      scale={selected ? 1.01 : 1.02}
      color={selected ? "red" : "black"}
      lineWidth={selected ? 5 : 1.5}
      transparent
      opacity={1}
    />
  );

  // Common props for disabling raycasting
  const raycastProps = disableRaycast ? { raycast: () => null } : {};

  // MediaCuller disabled — visible pop-in when turning camera.
  // To re-enable, uncomment the MediaCuller wrapping below and the import above.
  // if (decor.type === "spotify" || decor.type === "youtube" || decor.type === "twitch")
  //   return (
  //     <MediaCuller isMobile={isMobile}>
  //       <DecoratedIframe decor={decor} edges={edges} disableRaycast={disableRaycast} />
  //     </MediaCuller>
  //   );
  if (decor.type === "spotify" || decor.type === "youtube" || decor.type === "twitch")
    return <DecoratedIframe decor={decor} edges={edges} disableRaycast={disableRaycast} />;
  if (decor.type === "image")
    return <DecoratedPlane decor={decor} edges={edges} disableRaycast={disableRaycast} />;
  // if (decor.type === "video")
  //   return (
  //     <MediaCuller isMobile={isMobile}>
  //       <DecoratedVideo decor={decor} video edges={edges} disableRaycast={disableRaycast} />
  //     </MediaCuller>
  //   );
  if (decor.type === "video")
    return <DecoratedVideo decor={decor} video edges={edges} disableRaycast={disableRaycast} />;

  return (
    <mesh {...raycastProps}>
      <planeGeometry args={[decor.width, decor.height]} />
      <meshBasicMaterial color={decor.option} side={0} />
      {edges}
    </mesh>
  );
}

function useDecorationDrag({
  isEditing,
  roomMesh,
  decorations,
  cameraControls,
  snappingForEditor,
  setPreviewPositions,
  setPreviewScales,
  editDecoration,
  previewScales,
  cancelDelete,
}) {
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const { camera, gl } = useThree();

  const [pointerDown, setPointerDown] = useState(null);
  const [pointerDownNormal, setPointerDownNormal] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [pointerPosition, setPointerPosition] = useState(null);
  const [scalingIndex, setScalingIndex] = useState(null);
  const [scaleStart, setScaleStart] = useState(null);

  // RAF throttling for smooth drag performance
  const rafId = useRef(null);
  const pendingUpdate = useRef(null);

  useEffect(() => {
    if (!isEditing || !roomMesh) return;
    cancelDelete?.();

    function handlePointerMove(event) {
      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects([roomMesh]);
      if (intersects.length === 0) return;
      const hit = intersects[0];

      // --- dragging ---
      if (pointerDown && dragIndex !== null) {
        const decor = decorations[dragIndex];
        if (hit.normal.distanceTo(pointerDownNormal) < 1e-6) {
          let point = hit.point.clone();
          if (snappingForEditor) {
            point.set(
              snapToGrid(point.x, 2.5),
              snapToGrid(point.y, 2.5),
              snapToGrid(point.z, 2.5)
            );
          }
          const isValid = isPointValidForRoomMesh(
            point,
            decor.width,
            decor.height,
            hit.normal,
            roomMesh
          );
          if (!isValid) return;

          const delta = point.clone().sub(pointerDown);
          const original = decorations[dragIndex].position;
          const previewPosition = [
            original[0] + delta.x,
            original[1] + delta.y,
            original[2] + delta.z,
          ];

          // Throttle state updates using requestAnimationFrame
          pendingUpdate.current = { type: 'drag', dragIndex, previewPosition, point };

          if (!rafId.current) {
            rafId.current = requestAnimationFrame(() => {
              if (pendingUpdate.current?.type === 'drag') {
                const { dragIndex, previewPosition, point } = pendingUpdate.current;
                setPreviewPositions((prev) => ({
                  ...prev,
                  [dragIndex]: previewPosition,
                }));
                setPointerPosition(point.clone());
              }
              rafId.current = null;
            });
          }
        }
      }

      // --- scaling ---
      if (scaleStart && scalingIndex !== null) {
        const decor = decorations[scalingIndex];
        const deltaY = hit.point.y - scaleStart.startY;
        // Scale 1:1 with mouse movement - dragging down by decoration height doubles size
        const newScale = Math.max(0.2, scaleStart.startScale - deltaY / decor.height);

        // Throttle state updates using requestAnimationFrame
        pendingUpdate.current = { type: 'scale', scalingIndex, newScale };

        if (!rafId.current) {
          rafId.current = requestAnimationFrame(() => {
            if (pendingUpdate.current?.type === 'scale') {
              const { scalingIndex, newScale } = pendingUpdate.current;
              setPreviewScales((prev) => ({ ...prev, [scalingIndex]: newScale }));
            }
            rafId.current = null;
          });
        }
      }
    }

    function handlePointerUp() {
      // --- finalize drag ---
      if (pointerDown && pointerPosition && dragIndex !== null) {
        const delta = pointerPosition.clone().sub(pointerDown);
        const original = decorations[dragIndex].position;
        const newPosition = [
          original[0] + delta.x,
          original[1] + delta.y,
          original[2] + delta.z,
        ];
        editDecoration(dragIndex, { position: newPosition });
      }

      // --- finalize scale ---
      if (scaleStart && scalingIndex !== null) {
        const previewScale = previewScales[scalingIndex] || 1;

        if (previewScale !== undefined) {
          const decor = decorations[scalingIndex];
          const newWidth = decor.width * previewScale;
          const newHeight = decor.height * previewScale;

          // Calculate the position offset to anchor from top-left corner
          const offsetX = (decor.width * (previewScale - 1)) / 2;
          const offsetY = -(decor.height * (previewScale - 1)) / 2;

          // Apply rotation to the offset
          const rotation = new THREE.Euler(...decor.rotation);
          const localOffset = new THREE.Vector3(offsetX, offsetY, 0);
          localOffset.applyEuler(rotation);

          const newPosition = [
            decor.position[0] + localOffset.x,
            decor.position[1] + localOffset.y,
            decor.position[2] + localOffset.z,
          ];

          editDecoration(scalingIndex, {
            width: newWidth,
            height: newHeight,
            position: newPosition
          });
        }
      }
      if (cameraControls) cameraControls.enabled = true;
      setPointerDown(null);
      setPointerDownNormal(null);
      setDragIndex(null);
      setPointerPosition(null);
      setScaleStart(null);
      setScalingIndex(null);
      setPreviewPositions({});
      setPreviewScales({});
      document.body.style.cursor = "auto";
    }
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      // Cancel any pending RAF on cleanup
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [
    pointerDown,
    pointerDownNormal,
    pointerPosition,
    dragIndex,
    decorations,
    camera,
    gl,
    roomMesh,
    snappingForEditor,
    isEditing,
    cameraControls,
    editDecoration,
    setPreviewPositions,
    scalingIndex,
    scaleStart,
    setPreviewScales,
    previewScales,
    cancelDelete,
  ]);

  return {
    raycaster,
    mouse,
    setPointerDown,
    setPointerDownNormal,
    setDragIndex,
    setScalingIndex,
    setScaleStart,
    scalingIndex,
  };
}

// Memoize Decoration component to prevent re-renders when other decorations change
// Only re-renders when its own props change (selection, position, scale)
const Decoration = React.memo(function Decoration({
  idx,
  decor,
  isSelected,
  isEditing,
  setSelectedIndex,
  setSelectedDecoration,
  setParentEnabled,
  raycaster,
  mouse,
  setPointerDown,
  setPointerDownNormal,
  setDragIndex,
  setScalingIndex,
  setScaleStart,
  roomMesh,
  cameraControls,
  previewPositions,
  previewScales,
  scalingIndex,
  setDeleteTarget,
  showLinkIndicators,
}) {
  const { camera, gl } = useThree();

  const handlePointerDown = (e) => {
    e.stopPropagation();
    if (!isEditing) return;
    setSelectedIndex(idx);
    setSelectedDecoration(decor, idx);
    setParentEnabled?.(false);
  };

  const handleDragStart = (e) => {
    e.stopPropagation();
    try {
      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects([roomMesh]);
      if (intersects.length === 0) return;
      const point = intersects[0].point;
      const normal = intersects[0].normal;
      setPointerDown(point.clone());
      setPointerDownNormal(normal.clone());
      setDragIndex(idx);
      if (cameraControls) cameraControls.enabled = false;
      document.body.style.cursor = "grabbing";
    } catch (error) {
      console.error(error);
    }
  };

  const handleScaleStart = (e) => {
    e.stopPropagation();
    const rect = gl.domElement.getBoundingClientRect();
    mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.current.setFromCamera(mouse.current, camera);
    const intersects = raycaster.current.intersectObjects([roomMesh]);
    if (intersects.length === 0) return;
    const point = intersects[0].point;
    setScalingIndex(idx);
    setScaleStart({
      startY: point.y,
      startScale: 1,
    });
    if (cameraControls) cameraControls.enabled = false;
    document.body.style.cursor = "ns-resize";
  };

  const scale = scalingIndex ? previewScales[idx] || 1 : 1;
  const basePosition = useMemo(
    () => new THREE.Vector3(...(previewPositions[idx] || decor.position)),
    [decor.position, idx, previewPositions]
  );
  const rotation = useMemo(() => new THREE.Euler(...decor.rotation), [decor]);

  // Calculate position offset to anchor scaling from top-left corner
  // Top-left is at [-width/2, height/2] in local space
  // When scaling by factor s, to keep top-left fixed, center moves by:
  // [width*(s-1)/2, -height*(s-1)/2] in local space
  const scaleOffset = useMemo(() => {
    if (!scalingIndex || scale === 1) return new THREE.Vector3(0, 0, 0);

    const offsetX = (decor.width * (scale - 1)) / 2;
    const offsetY = -(decor.height * (scale - 1)) / 2;

    // Apply rotation to the offset
    const localOffset = new THREE.Vector3(offsetX, offsetY, 0);
    localOffset.applyEuler(rotation);

    return localOffset;
  }, [scalingIndex, scale, decor.width, decor.height, rotation]);

  const position = useMemo(() => {
    return basePosition.clone().add(scaleOffset);
  }, [basePosition, scaleOffset]);

  // Compute front normal
  const front = new THREE.Vector3(0, 0, 1).applyEuler(rotation);
  const toOrigin = new THREE.Vector3().subVectors(
    new THREE.Vector3(0, 0, 0),
    position
  );

  const dot = front.dot(toOrigin);
  const needsFlip = dot < 0; // if facing away from origin

  // Offset each decoration slightly toward room center to prevent z-fighting
  // Later decorations (higher index) sit slightly in front of earlier ones
  const zFightOffset = front.clone().multiplyScalar(idx * 0.05);

  return (
    <group
      position={position.clone().add(zFightOffset)}
      rotation={rotation}
      scale={scalingIndex ? scale : 1}
    >
      <group
        name={idx.toString()}
        userData={{
          id: idx,
          isDecoration: true,
          decorationType: decor.type,
          link: decor.link,
          hoverText: decor.hoverText
        }}
        onPointerDown={isEditing ? handlePointerDown : undefined}
        rotation={needsFlip ? [0, Math.PI, 0] : [0, 0, 0]}
      >
        <DecorationMesh
          decor={decor}
          selected={isEditing && isSelected}
          disableRaycast={false}
        />
        {/* Interactive link indicator - blue outline in player mode for clickable decorations */}
        {!isEditing && showLinkIndicators && (decor.link || decor.type === "youtube" || decor.type === "spotify" || decor.type === "twitch") && (
          <LinkIndicatorEdges decor={decor} />
        )}
      </group>

      {isEditing && isSelected && (
        <group>
          {/* delete handle */}
          <Html
            position={[decor.width / 2 + 1, decor.height / 2 + 1, 0.5]}
            center
            zIndexRange={[10000, 0]}
            style={{ pointerEvents: "auto" }}
          >
            <button
              onPointerDown={(e) => {
                e.stopPropagation();
                setDeleteTarget();
              }}
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                border: "none",
                background: "#ef4444",
                color: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                touchAction: "manipulation",
              }}
              aria-label="Delete decoration"
            >
              <IoClose size={24} />
            </button>
          </Html>
          {/* drag handle */}
          <Html
            position={[0, 0, 0.5]}
            center
            zIndexRange={[10000, 0]}
            style={{ pointerEvents: "auto" }}
          >
            <button
              onPointerDown={handleDragStart}
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                border: "none",
                background: "#3b82f6",
                color: "white",
                cursor: "grab",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                touchAction: "manipulation",
              }}
              aria-label="Move decoration"
            >
              <IoMove size={24} />
            </button>
          </Html>
          {/* scale handle */}
          <Html
            position={[-decor.width / 2 - 1, -decor.height / 2 - 1, 0.5]}
            center
            zIndexRange={[10000, 0]}
            style={{ pointerEvents: "auto" }}
          >
            <button
              onPointerDown={handleScaleStart}
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                border: "none",
                background: "#22c55e",
                color: "white",
                cursor: "ns-resize",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                touchAction: "manipulation",
              }}
              aria-label="Scale decoration"
            >
              <IoExpand size={24} />
            </button>
          </Html>
        </group>
      )}
    </group>
  );
});

function Decorations({ isEditing = false, setParentEnabled, roomMesh }) {
  const decorations = useRoomStore((s) => s.decorations);
  const removeDecoration = useRoomStore((s) => s.removeDecoration);
  const editDecoration = useRoomStore((s) => s.editDecoration);
  const cameraControls = useModelStore((s) => s.cameraControls);
  const setSelectedDecoration = useUserInterfaceStore(
    (s) => s.setSelectedDecoration
  );
  const [selectedIndex, setSelectedIndex] = useState(null);
  const selectedDecorationIndex = useUserInterfaceStore(
    (s) => s.selectedDecorationIndex
  );
  const showLinkIndicators = useUserInterfaceStore(
    (s) => s.showLinkIndicators
  );
  const snappingForEditor = useUserInterfaceStore(
    (s) => s.snappingForEditor
  );
  const [previewPositions, setPreviewPositions] = useState({});
  const [previewScales, setPreviewScales] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(-1); // null or idx

  // const handleDelete = () => setShowDeleteModal(true);

  const confirmDelete = () => {
    setSelectedIndex(null);
    setSelectedDecoration(null);

    removeDecoration(deleteTarget);
    setDeleteTarget(-1);
  };

  const cancelDelete = useCallback(() => {
    if (deleteTarget > -1) setDeleteTarget(-1);
  }, [deleteTarget]);
  const { gl } = useThree();
  useEffect(() => {
    const canvas = gl.domElement;
    const modalOpen = deleteTarget > -1;

    canvas.style.pointerEvents = modalOpen ? "none" : "auto";
    if (cameraControls) cameraControls.enabled = !modalOpen;

    return () => {
      canvas.style.pointerEvents = "auto";
      if (cameraControls) cameraControls.enabled = true;
    };
  }, [deleteTarget, gl, cameraControls, setParentEnabled]);

  // Sync local selectedIndex with store's selectedDecorationIndex
  useEffect(() => {
    if (selectedDecorationIndex < 0) {
      setSelectedIndex(null);
    } else {
      setSelectedIndex(selectedDecorationIndex);
    }
  }, [selectedDecorationIndex]);

  useEffect(() => {
    const canvas = gl.domElement;
    const modalOpen = deleteTarget > -1;

    canvas.style.pointerEvents = modalOpen ? "none !important" : "auto";
    if (cameraControls) cameraControls.enabled = !modalOpen;

    return () => {
      canvas.style.pointerEvents = "auto";
      if (cameraControls) cameraControls.enabled = true;
    };
  }, [deleteTarget, gl, cameraControls]);
  const {
    raycaster,
    mouse,
    setPointerDown,
    setPointerDownNormal,
    setDragIndex,
    setScalingIndex,
    setScaleStart,
    scalingIndex,
  } = useDecorationDrag({
    isEditing,
    roomMesh,
    decorations,
    editDecoration,
    cameraControls,
    snappingForEditor,
    setPreviewPositions,
    setPreviewScales,
    previewScales,
    cancelDelete: () => {},
  });

  useEffect(() => {
    if (!isEditing) {
      setSelectedIndex(null);
      setSelectedDecoration(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);
  useEffect(() => {
    if (selectedIndex !== deleteTarget && deleteTarget > -1) cancelDelete();
  }, [selectedIndex, deleteTarget, cancelDelete]);

  return (
    <group scale={0.99}>
      {decorations.map((decor, idx) => (
        <Decoration
          key={idx}
          idx={idx}
          decor={decor}
          isEditing={isEditing}
          isSelected={idx === selectedIndex}
          setSelectedIndex={setSelectedIndex}
          setSelectedDecoration={setSelectedDecoration}
          setParentEnabled={setParentEnabled}
          raycaster={raycaster}
          mouse={mouse}
          setPointerDown={setPointerDown}
          setPointerDownNormal={setPointerDownNormal}
          setDragIndex={setDragIndex}
          setScalingIndex={setScalingIndex}
          setScaleStart={setScaleStart}
          roomMesh={roomMesh}
          cameraControls={cameraControls}
          removeDecoration={removeDecoration}
          previewPositions={previewPositions}
          previewScales={previewScales}
          scalingIndex={idx === scalingIndex}
          setDeleteTarget={() => setDeleteTarget(idx)}
          showLinkIndicators={showLinkIndicators}
        />
      ))}

      {deleteTarget > -1 && (
        <DeleteModal
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
        />
      )}
    </group>
  );
}

export default Decorations;
